import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Check, CheckCircle2, Save, RotateCcw, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { FitnessWizardProvider, useFitnessWizard } from '../contexts/FitnessWizardContext';
import axios from 'axios';
import { toast } from 'sonner';
import { captureAttribution } from '../utils/attribution';

import FitnessPrimaryContact from '../components/fitness-wizard/FitnessPrimaryContact';
import FitnessBusinessInfo from '../components/fitness-wizard/FitnessBusinessInfo';
import FitnessClubType from '../components/fitness-wizard/FitnessClubType';
import FitnessLocations from '../components/fitness-wizard/FitnessLocations';
import FitnessMembership from '../components/fitness-wizard/FitnessMembership';
import FitnessCoverage from '../components/fitness-wizard/FitnessCoverage';
import FitnessClaimsHistory from '../components/fitness-wizard/FitnessClaimsHistory';
import FitnessFraudWarranty from '../components/fitness-wizard/FitnessFraudWarranty';
import FitnessAdditionalInsured from '../components/fitness-wizard/FitnessAdditionalInsured';
import FitnessReviewSubmit from '../components/fitness-wizard/FitnessReviewSubmit';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const STEPS = [
  { number: 1, title: 'Contact', component: FitnessPrimaryContact },
  { number: 2, title: 'Business', component: FitnessBusinessInfo },
  { number: 3, title: 'Services', component: FitnessClubType },
  { number: 4, title: 'Locations', component: FitnessLocations },
  { number: 5, title: 'Members', component: FitnessMembership },
  { number: 6, title: 'Coverage', component: FitnessCoverage },
  { number: 7, title: 'Claims', component: FitnessClaimsHistory },
  { number: 8, title: 'Warranty', component: FitnessFraudWarranty },
  { number: 9, title: "Add'l Insured", component: FitnessAdditionalInsured },
  { number: 10, title: 'Review', component: FitnessReviewSubmit }
];

const FitnessWizardContent = ({ affiliate }) => {
  const { currentStep, prevStep, formData, showResume, resumeDraft, discardDraft } = useFitnessWizard();
  const CurrentStepComponent = STEPS[currentStep - 1]?.component;
  const hasPrefill = !!formData.risk_assessment_id;

  // Resume prompt
  if (showResume) {
    return (
      <div className="min-h-screen bg-[#F7F8FA] flex items-center justify-center px-6">
        <div className="max-w-md w-full bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-8 relative overflow-hidden" data-testid="resume-draft-dialog">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
          <div className="flex items-center gap-3 mb-4">
            <RotateCcw className="h-6 w-6 text-[#7B6FE8]" />
            <h2 className="text-xl font-black text-[#1A1A1A] uppercase" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Resume Your Application?</h2>
          </div>
          <p className="text-sm text-[#6B7280] mb-6">We found a saved draft from your previous session. Would you like to pick up where you left off?</p>
          <div className="flex gap-3">
            <Button onClick={resumeDraft} data-testid="resume-draft-btn" className="flex-1 bg-[#7B6FE8] text-white hover:bg-[#6B5CE7] font-semibold">
              <RotateCcw className="mr-2 h-4 w-4" /> Resume Draft
            </Button>
            <Button onClick={discardDraft} variant="outline" data-testid="discard-draft-btn" className="flex-1 border-[#E5E7EB] text-[#6B7280] hover:bg-[#F3F4F6]">
              <X className="mr-2 h-4 w-4" /> Start Fresh
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <header className="bg-[#232323] sticky top-0 z-50">
        <div className="h-[3px] bg-gradient-to-r from-[#E040FB] via-[#FF6B35] to-[#FFD93D]"></div>
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo-light.png" alt="Players Health" className="h-8" />
              {affiliate.logo_url && (
                <>
                  <div className="h-6 w-px bg-white/20"></div>
                  <img src={affiliate.logo_url} alt={affiliate.name} className="h-6" />
                </>
              )}
            </div>
            <div className="flex items-center gap-3 text-sm" style={{fontFamily: "'Inter', sans-serif"}}>
              <span className="text-white/30 hidden sm:inline-flex items-center gap-1"><Save className="h-3 w-3" />Auto-saved</span>
              <span className="text-white/50">Fitness Studio Application</span>
            </div>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-[#232323] border-b border-white/10">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-white/80">Step {currentStep} of {STEPS.length}</span>
            <span className="text-sm text-[#7B6FE8] font-semibold">{STEPS[currentStep - 1]?.title}</span>
          </div>
          <div className="h-2 bg-white/10 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(currentStep / STEPS.length) * 100}%`, background: 'linear-gradient(90deg, #6B5CE7, #4ECDC4)' }}
            />
          </div>
          
          {/* Step indicators */}
          <div className="hidden md:flex items-center justify-between mt-4">
            {STEPS.map((step, idx) => (
              <div 
                key={step.number}
                className={`flex flex-col items-center ${
                  idx < currentStep - 1 ? 'text-[#4ECDC4]' :
                  idx === currentStep - 1 ? 'text-[#7B6FE8]' : 'text-white/30'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium mb-1 ${
                  idx < currentStep - 1 ? 'bg-[#4ECDC4]/20 text-[#4ECDC4]' :
                  idx === currentStep - 1 ? 'bg-[#7B6FE8]/20 text-[#7B6FE8] ring-2 ring-[#7B6FE8]' : 
                  'bg-white/5 text-white/30'
                }`}>
                  {idx < currentStep - 1 ? <Check className="h-4 w-4" /> : step.number}
                </div>
                <span className="text-xs hidden lg:block">{step.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-8">
        {hasPrefill && currentStep <= 2 && (
          <div className="flex items-center gap-2 p-3 bg-[#ECFDF5] border border-[#A7F3D0] rounded-lg mb-4" data-testid="prefill-notice">
            <CheckCircle2 className="h-4 w-4 text-[#065F46] flex-shrink-0" />
            <p className="text-sm text-[#065F46]">Some fields have been pre-filled from your Risk Assessment. Please review and update as needed.</p>
          </div>
        )}
        {currentStep > 1 && (
          <Button
            variant="ghost"
            onClick={prevStep}
            className="mb-6 text-[#6B7280] hover:text-[#7B6FE8] hover:bg-[#EEF0FF]"
            data-testid="back-button"
          >
            <ChevronLeft className="mr-1 h-4 w-4" />
            Back
          </Button>
        )}

        <div className="bg-white border border-[#E5E7EB] rounded-xl shadow-lg p-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-[#6B5CE7] to-[#4ECDC4]"></div>
          {CurrentStepComponent && <CurrentStepComponent affiliate={affiliate} />}
        </div>
      </main>
    </div>
  );
};

const FitnessWizard = ({ directAccess = false }) => {
  const { affiliateSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [affiliate, setAffiliate] = useState(null);
  const [prefillData, setPrefillData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Capture UTM lead-source attribution on landing (covers /apply-fitness/:slug and /quote/fitness)
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    const loadAffiliate = async () => {
      if (directAccess) {
        const ref = searchParams.get('ref');
        if (ref) {
          try {
            const response = await axios.get(`${API_URL}/affiliates/${ref}`);
            setAffiliate(response.data);
          } catch (e) {
            setAffiliate({
              id: 'players-health-direct',
              slug: 'players-health-direct',
              name: 'Players Health',
              flow_template: 'BOUTIQUE_FITNESS_V1'
            });
          }
        } else {
          setAffiliate({
            id: 'players-health-direct',
            slug: 'players-health-direct',
            name: 'Players Health',
            flow_template: 'BOUTIQUE_FITNESS_V1'
          });
        }
      } else {
        try {
          const response = await axios.get(`${API_URL}/affiliates/${affiliateSlug}`);
          if (response.data.flow_template !== 'BOUTIQUE_FITNESS_V1') {
            toast.error('This partner uses a different application flow');
            navigate(`/apply/${affiliateSlug}`);
            return;
          }
          setAffiliate(response.data);
        } catch (error) {
          toast.error('Partner not found');
          navigate('/');
          return;
        }
      }

      // Fetch prefill data from risk assessment if available
      const riskAssessmentId = searchParams.get('risk_assessment_id');
      if (riskAssessmentId) {
        try {
          const res = await axios.get(`${API_URL}/risk-assessment/${riskAssessmentId}/prefill`);
          setPrefillData(res.data);
        } catch (err) {
          // Non-blocking — just proceed without prefill
        }
      }

      setLoading(false);
    };
    loadAffiliate();
  }, [affiliateSlug, navigate, directAccess, searchParams]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#232323]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4ECDC4]"></div>
      </div>
    );
  }

  if (!affiliate) return null;

  return (
    <FitnessWizardProvider initialData={prefillData}>
      <FitnessWizardContent affiliate={affiliate} />
    </FitnessWizardProvider>
  );
};

export default FitnessWizard;
